import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { create } from 'zustand';

import { getApiErrorMessage } from '@/lib/api/client';
import { getData } from '@/lib/api/client-v2';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import type { Contract, ContractV2 } from '@/types/api/contract';
import { ENDPOINTS, PDF } from '@/types/api/endpoints';

const orUndefined = <T>(value: T | null): T | undefined => value ?? undefined;

/**
 * Ramène la fiche v2 aux noms de la v1, que lisent les écrans.
 *
 * Les données gardent leur valeur Belair (dates `yyyy-MM-dd`, libellés) : seul le nom change. Le
 * mémo de l'ancien véhicule, qui était un lien servi par la v1, se construit sur son adhésion.
 */
export const toContract = (contract: ContractV2): Contract => {
    const { subscriber, vehicle, previous_vehicle: previous, situation, hamon, broker } = contract;

    return {
        id: contract.contract_id,
        numeroPolice: orUndefined(contract.policy_number),
        compagnie: orUndefined(contract.company),
        flotte: contract.flotte,
        apporteur1: contract.apporteur_id,
        apporteurNom: orUndefined(contract.apporteur_name),
        apporteurs: broker
            ? {
                  apporteur1: {
                      raisonSociale: orUndefined(broker.name),
                      rue1: orUndefined(broker.address.street1),
                      codePostal: orUndefined(broker.address.postal_code),
                      ville: orUndefined(broker.address.city),
                      phone: orUndefined(broker.phone),
                      email_principal: orUndefined(broker.email),
                  },
              }
            : undefined,
        souscripteur: {
            id: orUndefined(subscriber.id),
            titre: orUndefined(subscriber.title),
            nom: orUndefined(subscriber.name),
            firstname: orUndefined(subscriber.firstname),
            lastname: orUndefined(subscriber.lastname),
            email: orUndefined(subscriber.email),
            phone: orUndefined(subscriber.phone),
            naissance: orUndefined(subscriber.birth_date),
            ville_naissance: orUndefined(subscriber.birth_city),
            pays_naissance: orUndefined(subscriber.birth_country),
            code_pays_naissance: orUndefined(subscriber.birth_country_code),
            nom_naissance: orUndefined(subscriber.birth_name),
            statut_juridique: orUndefined(subscriber.statut_juridique),
            num_siren_siret: orUndefined(subscriber.siren_siret),
            adresse: {
                rue1: orUndefined(subscriber.address.street1),
                rue2: orUndefined(subscriber.address.street2 ?? null),
                rue3: orUndefined(subscriber.address.street3 ?? null),
                codePostal: orUndefined(subscriber.address.postal_code),
                ville: orUndefined(subscriber.address.city),
            },
        },
        tiers_payeur: contract.tiers_payeur
            ? {
                  nom: orUndefined(contract.tiers_payeur.name),
                  prenom: orUndefined(contract.tiers_payeur.firstname),
                  nom_naissance: orUndefined(contract.tiers_payeur.birth_name),
                  naissance: orUndefined(contract.tiers_payeur.birth_date),
                  ville_naissance: orUndefined(contract.tiers_payeur.birth_city),
                  relation: orUndefined(contract.tiers_payeur.relation),
                  raison_autre: orUndefined(contract.tiers_payeur.reason_other),
              }
            : null,
        vehicule: {
            codeProduit: orUndefined(vehicle.product_code),
            libelleProduit: orUndefined(vehicle.product_label),
            marque: orUndefined(vehicle.brand),
            modele: orUndefined(vehicle.model),
            immatriculation: orUndefined(vehicle.registration),
            VIN: orUndefined(vehicle.vin),
            usage: orUndefined(vehicle.usage),
            type: orUndefined(vehicle.type),
            cylindree: orUndefined(vehicle.cylinder),
            crm: orUndefined(vehicle.crm),
            lieuStationnement: orUndefined(vehicle.parking_location),
            lieuStationnementCodePostal: orUndefined(vehicle.parking_postal_code),
            lieuStationnementVille: orUndefined(vehicle.parking_city),
            transitoire: vehicle.transitional,
            vente_old_vehicule: vehicle.sold_previous_vehicle,
        },
        old_vehicule: previous
            ? {
                  marque: orUndefined(previous.brand),
                  modele: orUndefined(previous.model),
                  immatriculation: orUndefined(previous.registration),
                  date_effet_piece: orUndefined(previous.coverage_start_date),
                  date_fin_piece: orUndefined(previous.coverage_end_date),
                  memo_link: previous.adhesion === null ? undefined : PDF.fleetVehicleMemo(previous.adhesion),
              }
            : null,
        situation: {
            statut: orUndefined(situation.status),
            statutCode: orUndefined(situation.status_code),
            motif: orUndefined(situation.reason),
            date: orUndefined(situation.effect_date),
            dateFin: orUndefined(situation.end_date),
            dateSituation: orUndefined(situation.situation_date),
            dateEffetAFN: orUndefined(situation.afn_effect_date),
            dateAffaireNouvelle: orUndefined(situation.new_business_date),
            dateEcheance: orUndefined(situation.date_echeance),
            echeance: orUndefined(situation.echeance),
            fractionnement: orUndefined(situation.payment_split),
        },
        garanties: contract.guarantees.map((guarantee) => ({
            code: orUndefined(guarantee.code),
            libelle: orUndefined(guarantee.label),
            type: orUndefined(guarantee.type),
            code_famille: orUndefined(guarantee.family_code),
            franchise: guarantee.franchise ?? '',
        })),
        hamon: hamon
            ? {
                  id: orUndefined(hamon.id),
                  confirm_resil: hamon.resiliation_confirmed === null ? undefined : hamon.resiliation_confirmed ? 'T' : 'F',
              }
            : null,
        prelevement: contract.premium.direct_debit,
        hivernages: contract.hivernages.map((hivernage) => ({
            dateDebut: orUndefined(hivernage.start_date),
            dateFin: orUndefined(hivernage.end_date),
            statut: orUndefined(hivernage.status),
            statutCode: orUndefined(hivernage.status_code),
            duree: orUndefined(hivernage.duration_days),
            periodeCourante: hivernage.current,
        })),
        // Comme en v1 : seul le solde des 120 jours épuisé laisse le contrat éligible (page visible,
        // activation impossible). Tout autre blocage (usage, DTA, département…) le rend non éligible.
        is_eligible_hivernage:
            contract.actions.hivernage?.status === 'available' ||
            contract.actions.hivernage?.reason?.code === 'SOLDE_HIVERNAGE_EPUISE',
        can_activate_hivernage: contract.actions.hivernage?.status === 'available',
        avantage: orUndefined(contract.avantage),
        adhesionPrincipale: orUndefined(contract.adhesion),
        nb_pieces: contract.last_piece_number,
        dernierCourrierCV: contract.last_green_card_sent_at,
        souscription_link: orUndefined(contract.subscription_link),
        upload_app_link: orUndefined(contract.documents_link),
        avenant_link: orUndefined(contract.avenant_link),
        actions: contract.actions,
    };
};

/** Contrats de l'utilisateur connecté (`GET /contracts`). */
export const useContracts = (enabled = true) =>
    useQuery({
        queryKey: queryKeys.contracts,
        queryFn: () => getData<ContractV2[]>(ENDPOINTS.contracts).then((contracts) => ({ data: contracts.map(toContract) })),
        staleTime: Infinity,
        enabled,
    });

/** Liste des contrats, vide tant qu'elle n'est pas chargée. */
export const useContractsData = (enabled = true): Contract[] => useContracts(enabled).data?.data ?? [];

/**
 * Contrat consulté en dernier.
 *
 * Plusieurs blocs hors de la fiche (en-tête, infobulle, barre latérale) s'adaptent au contrat
 * ouvert : la fiche l'enregistre ici, eux le lisent par `useSelectedContract`.
 */
interface SelectedContractState {
    contractId: string | null;
    setContractId: (id: string | number | null) => void;
}

export const useSelectedContractStore = create<SelectedContractState>()((set) => ({
    contractId: null,
    setContractId: (id) => set({ contractId: id === null ? null : String(id) }),
}));

const fetchContract = (id: string | number) => getData<ContractV2>(ENDPOINTS.contract(id)).then(toContract);

/** Lecture brute d'un contrat, sans popin ni sélection (ex : contrôle d'éligibilité). */
export const useContractQuery = (id: string | number | null | undefined) =>
    useQuery({
        queryKey: queryKeys.contract(id),
        queryFn: () => fetchContract(id as string | number),
        enabled: id !== null && id !== undefined && id !== '',
        retry: false,
    });

/**
 * Détail d'un contrat, qui devient le contrat sélectionné (cf. `useSelectedContract`).
 *
 * L'échec de lecture est signalé par une popin, comme auparavant.
 */
export const useContract = (id: string | number | null | undefined) => {
    const setContractId = useSelectedContractStore((state) => state.setContractId);

    useEffect(() => {
        if (id !== null && id !== undefined && id !== '') setContractId(id);
    }, [id, setContractId]);

    return useQuery({
        queryKey: queryKeys.contract(id),
        queryFn: () =>
            fetchContract(id as string | number).catch((error: unknown) => {
                void notify({
                    html: '<p>Une erreur est survenue lors de la récupération du contrat.</p>',
                    icon: 'error',
                    title: 'Attention !',
                    confirmButtonText: "J'ai compris",
                });
                throw error;
            }),
        enabled: id !== null && id !== undefined && id !== '',
        retry: false,
    });
};

/** Dernier contrat ouvert (fiche contrat), ou `undefined` hors fiche. */
export const useSelectedContract = (): Contract | undefined => {
    const id = useSelectedContractStore((state) => state.contractId);
    return useContractQuery(id).data;
};

export { getApiErrorMessage };
